import React, { MouseEvent, useState } from 'react';
import { SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import dayjs from 'dayjs';
import ToolTip from '@/components/@IntranetLibrary/ToolTip';
import { RemoteFile } from '@/components/file-thumbnail/utils';
import {
  getFileIconSrc,
  getFileNameAndExt,
  isImageFile,
  getExtensionIcon,
} from '@/components/AttachmentFile/utils';
import {
  ItemCardWrapper,
  ItemCard,
  ThumbnailWrapper,
  Thumbnail,
  ThumbnailOverlay,
  FileIcon,
  InfoBlock,
  FileName,
  FileMeta,
  DeleteButton,
  DownloadButton,
} from '@/components/AttachmentFile/styled';
import { formatFileSize, downloadFile } from '@/utils';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { imgUrlChk } from '@/utils/function';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import { useFileViewer } from '@/hooks/useFileViewer';
import useLayoutSize from '@/hooks/useLayoutSize';

const DownloadIconSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <g clipPath="url(#clip0_7007_3227)">
      <path
        d="M4 16H16"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.001 11.9999V4"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.001 11.9999L14 8.79993"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.99902 12L6 8.80005"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_7007_3227">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export type AttachmentFileStatus = 'success' | 'loading' | 'error';
export type AttachmentFileProps<CustomFile> = {
  readonly file: CustomFile;
  readonly status?: AttachmentFileStatus;
  readonly disabled?: boolean;
  readonly isDeletable: boolean;
  readonly isPreviewable: boolean;
  readonly onSuccess?: (file: CustomFile) => void;
  readonly listeners?: any;
  readonly sx?: SxProps<Theme>;
};

const AttachmentFile = <CustomFile extends RemoteFile>({
  file,
  status = 'success',
  disabled,
  isDeletable,
  isPreviewable,
  onSuccess,
  listeners = {},
  sx,
}: AttachmentFileProps<CustomFile>) => {
  const { name: fileName, link } = file;
  const { openFileViewer } = useFileViewer();
  const { isDesktop } = useLayoutSize();
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const fileUuid = file.uuid ?? file.fileUuid;
  const isLoading = status === 'loading';
  const buttonDisabled = isLoading || disabled;

  const { name, ext } = getFileNameAndExt(fileName);
  const extWithoutDot = ext.replace('.', '').toLowerCase();
  const isImage = isImageFile(fileName);
  const iconSrcName = getFileIconSrc(fileName);

  // 파일 메타데이터 (확장 필드)
  type FileWithMeta = RemoteFile & {
    size?: number | string;
    fileSize?: number | string;
    createdAt?: string | Date;
    updatedAt?: string | Date;
  };
  const fileMeta = file as FileWithMeta;
  const rawFileSize = fileMeta.fileSize ?? fileMeta.size;
  const fileSize =
    rawFileSize == null || rawFileSize === '' ? undefined : Number(rawFileSize);
  const rawDate = fileMeta.createdAt ?? fileMeta.updatedAt;
  const fileDate = rawDate
    ? dayjs(rawDate).format('YYYY-MM-DD HH:mm:ss')
    : null;

  const handleDelete = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onSuccess?.(file);
  };

  const handleDownload = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isDownloading) return;

    // React Native WebView 환경 처리
    if (
      (window.isRNWebview || window.isRNWebview2) &&
      window.ReactNativeWebView
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          actionType: 'OPEN_BROWSER',
          url: link,
        }),
      );
      return;
    }

    setIsDownloading(true);
    try {
      await downloadFile({
        name: fileName,
        link,
        uuid: fileUuid,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePreviewClick = () => {
    if (!isPreviewable) return;
    // 미리보기 클릭 시 호버 툴팁이 뷰어 위에 겹쳐 남는 문제 방지 — 즉시 닫는다.
    setIsHovered(false);
    if (fileUuid) {
      openFileViewer({
        uuid: fileUuid,
        name: file.name,
        link: file.link,
        openAiPanel: true,
      });
    } else {
      window.open(file.link, '_blank');
    }
  };

  const clickable = isPreviewable && !isLoading;
  const showHoverEffect = !isLoading && isHovered;
  // uuid가 없으면 뷰어 대신 새 창으로 링크를 여는 동작이라 문구를 구분한다.
  // '링크열기'는 PC에서만 노출한다.
  const isLinkOpen = !fileUuid;
  const showPreviewOverlay =
    clickable && isHovered && (!isLinkOpen || isDesktop);
  const previewOverlayLabel = isLinkOpen ? '링크열기' : '미리보기';
  // 카드 강조는 미리보기 영역에 호버했을 때만 적용한다.
  const showCardHighlight = clickable && showHoverEffect;

  return (
    <ItemCardWrapper hovered={showCardHighlight} sx={sx} {...listeners}>
      <ItemCard
        hovered={showCardHighlight}
        sx={{ opacity: isLoading ? 0.5 : 1 }}
      >
        {isDeletable && !isLoading && (
          <DeleteButton
            type="button"
            aria-label="첨부 삭제"
            onClick={handleDelete}
            disabled={buttonDisabled}
          >
            <img src="/icons/approval/close.svg" alt="" aria-hidden="true" />
          </DeleteButton>
        )}

        <ToolTip title={fileName} open={showHoverEffect} disableHoverListener>
          <ThumbnailWrapper
            clickable={clickable}
            onClick={clickable ? handlePreviewClick : undefined}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Thumbnail isImage={isImage && !isLoading}>
              {isLoading ? (
                <Spinner theme={'black'} />
              ) : isImage ? (
                <ImageWithErrorHandling
                  src={imgUrlChk(link)}
                  width={152}
                  height={76}
                  alt={`${name} Image`}
                  errorImage={`${iconSrcName}.png`}
                  className={'no-drag'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <FileIcon
                  src={getExtensionIcon(extWithoutDot)}
                  alt=""
                  aria-hidden="true"
                  onError={(e) => {
                    e.currentTarget.src = `${iconSrcName}.png`;
                  }}
                />
              )}
            </Thumbnail>
            {showPreviewOverlay && (
              <ThumbnailOverlay>{previewOverlayLabel}</ThumbnailOverlay>
            )}
            {!isLoading && (
              <DownloadButton
                type="button"
                aria-label="다운로드"
                onClick={handleDownload}
                disabled={isDownloading || buttonDisabled}
                title="다운로드"
              >
                {isDownloading ? (
                  <Spinner theme="black" />
                ) : (
                  <DownloadIconSvg />
                )}
              </DownloadButton>
            )}
          </ThumbnailWrapper>
        </ToolTip>

        <InfoBlock>
          <FileName as="span" title={fileName}>
            <span className="name">{name}</span>
            <span className="ext">{ext}</span>
          </FileName>
          {fileDate && <FileMeta as="span">{fileDate}</FileMeta>}
          {fileSize !== undefined && Number.isFinite(fileSize) && (
            <FileMeta as="span">{formatFileSize(fileSize)}</FileMeta>
          )}
        </InfoBlock>
      </ItemCard>
    </ItemCardWrapper>
  );
};

export default AttachmentFile;
